import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../services/auth.service';

class BucharestDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: object): string {
    if (displayFormat === BUCHAREST_DATE_FORMATS.display.dateInput) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    }
    return super.format(date, displayFormat);
  }
}

const BUCHAREST_DATE_FORMATS = {
  parse: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

interface DealData {
  dealName: string;
  dealStage: string;
  amount?: number;
  dealOwner: number;
  contactIds: number[];
  companyIds: number[];
  closeDate: string;
}

@Component({
  selector: 'app-add-deal-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TitleCasePipe
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'ro-RO' },
    { provide: DateAdapter, useClass: BucharestDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: BUCHAREST_DATE_FORMATS }
  ],
  templateUrl: './add-deal-modal.component.html',
  styleUrl: './add-deal-modal.component.scss'
})
export class AddDealModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isVisible: boolean = false;
  @Input() contacts: any[] = [];
  @Input() companies: any[] = [];
  @Input() deal: any = null;
  @Input() mode: 'create' | 'edit' = 'create';
  /**
   * In create mode, pre-select a contact in the "Select Contact" field
   * (e.g. when the modal is opened from a contact row's "Add deal" action).
   * Ignored in edit mode — the deal's own contact_id wins there.
   */
  @Input() preselectedContactId: number | string | null = null;
  /**
   * In create mode, pre-select a company in the "Select Company" field
   * (e.g. when the modal is opened from a company row's "Add deal" action).
   */
  @Input() preselectedCompanyId: number | string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<DealData>();

  dealForm: FormGroup;
  loading: boolean = false;
  user: any = null;
  filteredContacts: any[] = [];
  filteredCompanies: any[] = [];
  minCloseDate: Date = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  get isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  }

  private destroy$ = new Subject<void>();

  // Options for dropdowns
  dealStages: string[] = ['early', 'progress', 'closing', 'won', 'renewal', 'lost', 'archived'];

  constructor(private fb: FormBuilder, private authService: AuthService) {
    this.dealForm = this.createForm();
    this.setupSelectionListeners();
  }

  ngOnInit(): void {
    this.applyFilters();

    // Get authenticated user
    this.authService.authUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });

    // Default close date is today
    const defaultCloseDate = new Date();
    defaultCloseDate.setHours(0, 0, 0, 0);
    this.dealForm.patchValue({
      closeDate: defaultCloseDate
    });

    // If in edit mode, populate the form
    if (this.mode === 'edit' && this.deal) {
      this.populateForm();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contacts'] || changes['companies']) {
      this.applyFilters();
    }

    // Edit mode — populate the form from the selected deal.
    if ((changes['deal'] || changes['mode']) && this.mode === 'edit' && this.deal) {
      this.populateForm();
      return;
    }

    // Create mode — fresh setup on:
    //   - first render or mode/deal change to create
    //   - modal opening (isVisible false → true), so reopens after a previous
    //     resetForm() don't leave dealStage/closeDate blank
    //   - either preselected* input changing while the modal is mounted
    const becameVisible = !!changes['isVisible'] && this.isVisible;
    const preselectionChanged = !!changes['preselectedContactId'] || !!changes['preselectedCompanyId'];
    const createModeChange = (!!changes['deal'] || !!changes['mode']) && this.mode === 'create';

    if (this.mode === 'create' && (becameVisible || preselectionChanged || createModeChange)) {
      this.resetCreateFormDefaults();
      this.applyPreselections();
      this.applyFilters();
    }
  }

  /** Reset to the initial create-mode state. Idempotent — safe to call on every open. */
  private resetCreateFormDefaults(): void {
    this.dealForm.reset();
    const defaultCloseDate = new Date();
    defaultCloseDate.setHours(0, 0, 0, 0);
    this.dealForm.patchValue({
      dealStage: 'early',
      closeDate: defaultCloseDate
    });
  }

  /**
   * Apply both pre-selections. Patches are routed through the form's
   * valueChanges, which feeds syncFromContact / syncFromCompany — the
   * sibling dropdown gets auto-narrowed to options compatible with the
   * chosen value, exactly as if the user had picked manually.
   */
  private applyPreselections(): void {
    if (this.preselectedContactId != null && this.preselectedContactId !== '') {
      this.dealForm.patchValue({ contactId: this.preselectedContactId });
    }
    if (this.preselectedCompanyId != null && this.preselectedCompanyId !== '') {
      this.dealForm.patchValue({ companyId: this.preselectedCompanyId });
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      dealName: ['', [Validators.required]],
      dealStage: ['early', [Validators.required]],
      amount: [0, [Validators.min(0)]],
      contactId: ['', [Validators.required]],
      companyId: ['', [Validators.required]],
      closeDate: ['', [Validators.required]]
    });
  }

  // Simplified - no complex listeners needed

  closeModal(): void {
    this.resetForm();
    this.close.emit();
  }

  clearContact(): void {
    this.dealForm.get('contactId')?.markAsTouched();
    this.dealForm.patchValue({ contactId: '' });
  }

  clearCompany(): void {
    this.dealForm.get('companyId')?.markAsTouched();
    this.dealForm.patchValue({ companyId: '' });
  }

  saveDeal(): void {
    if (this.dealForm.valid) {
      this.loading = true;

      const formValue = this.dealForm.value;
      const closeDateValue = formValue.closeDate instanceof Date
        ? this.formatDateToIso(formValue.closeDate)
        : formValue.closeDate;
      const dealData: DealData = {
        dealName: formValue.dealName,
        dealStage: formValue.dealStage,
        amount: formValue.amount || 0,
        dealOwner: this.user?.id || 0,
        contactIds: formValue.contactId ? [formValue.contactId] : [],
        companyIds: formValue.companyId ? [formValue.companyId] : [],
        closeDate: closeDateValue
      };

      // console.log('Saving deal with data:', dealData);

      // Simulate API call delay
      setTimeout(() => {
        this.save.emit(dealData);
        this.loading = false;
        this.resetForm();
        this.closeModal();
      }, 1000);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.dealForm.controls).forEach(key => {
        this.dealForm.controls[key].markAsTouched();
      });
    }
  }

  private resetForm(): void {
    this.dealForm.reset();
    this.loading = false;
    this.applyFilters();
  }

  private setupSelectionListeners(): void {
    this.dealForm.get('contactId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncFromContact());

    this.dealForm.get('companyId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncFromCompany());
  }

  private syncFromContact(): void {
    const contactId = this.dealForm.get('contactId')?.value;
    this.filteredCompanies = contactId
      ? this.getCompaniesForContact(contactId)
      : [...this.companies];

    const companyId = this.dealForm.get('companyId')?.value;
    if (companyId && !this.filteredCompanies.some(c => c.id == companyId)) {
      this.dealForm.patchValue({ companyId: '' }, { emitEvent: false });
    }

    const currentCompanyId = this.dealForm.get('companyId')?.value;
    this.filteredContacts = currentCompanyId
      ? this.getContactsForCompany(currentCompanyId)
      : [...this.contacts];
  }

  private syncFromCompany(): void {
    const companyId = this.dealForm.get('companyId')?.value;
    this.filteredContacts = companyId
      ? this.getContactsForCompany(companyId)
      : [...this.contacts];

    const contactId = this.dealForm.get('contactId')?.value;
    if (contactId && !this.filteredContacts.some(c => c.id == contactId)) {
      this.dealForm.patchValue({ contactId: '' }, { emitEvent: false });
    }

    const currentContactId = this.dealForm.get('contactId')?.value;
    this.filteredCompanies = currentContactId
      ? this.getCompaniesForContact(currentContactId)
      : [...this.companies];
  }

  private applyFilters(): void {
    const contactId = this.dealForm.get('contactId')?.value;
    const companyId = this.dealForm.get('companyId')?.value;
    this.filteredCompanies = contactId
      ? this.getCompaniesForContact(contactId)
      : [...this.companies];
    this.filteredContacts = companyId
      ? this.getContactsForCompany(companyId)
      : [...this.contacts];
  }

  private getCompaniesForContact(contactId: number | string): any[] {
    const contact = this.contacts.find(c => c.id == contactId);
    if (!contact?.company_contacts?.length) return [];
    const ids = contact.company_contacts
      .map((cc: any) => cc.company?.id)
      .filter((id: any) => id);
    return this.companies.filter(c => ids.includes(c.id));
  }

  private getContactsForCompany(companyId: number | string): any[] {
    const company = this.companies.find(c => c.id == companyId);
    if (!company?.company_contacts?.length) return [];
    const ids = company.company_contacts
      .map((cc: any) => cc.contact?.id)
      .filter((id: any) => id);
    return this.contacts.filter(c => ids.includes(c.id));
  }

  private populateForm(): void {
    if (this.deal) {
      // Find the matching contact and company IDs from the deal
      const contactId = this.deal.contact_id || this.deal.contact?.id;
      const companyId = this.deal.company_id || this.deal.company?.id;

      // Format the close date for the input field
      const closeDate: Date | null = this.deal.close_date ? new Date(this.deal.close_date) : null;

      this.dealForm.patchValue({
        dealName: this.deal.name || '',
        dealStage: this.deal.deal_stage || 'early',
        amount: this.deal.deal_value || 0,
        contactId: contactId || '',
        companyId: companyId || '',
        closeDate: closeDate
      }, { emitEvent: false });

      this.applyFilters();
    }
  }

  private formatDateToIso(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
